import { hexToRGBAValues } from './color-detection';
import palettesRaw from '@atlaskit/tokens/palettes-raw';
import type { TransformedToken } from 'style-dictionary';

type PaletteCategory =
  | 'blue'
  | 'purple'
  | 'red'
  | 'magenta'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'teal'
  | 'light mode neutral'
  | 'dark mode neutral';

type PaletteToken = {
  attributes: {
    group: 'palette';
    category: PaletteCategory;
  };
};

export type PaletteGroup = {
  name: 'colors' | 'light mode neutrals' | 'dark mode neutrals';
  subgroups: Array<{
    name: string;
    tokens: TransformedToken[];
  }>;
};

export interface TransformedTokenPalette extends TransformedToken {
  attributes: TransformedToken['attributes'] & PaletteToken['attributes'];
}

/**
 * Find token type.
 */
const findGroup = (groups: PaletteGroup[], token: TransformedTokenPalette) => {
  const type = getGroupName(token);

  return groups.find(({ name }) => name === type);
};

/**
 * Find token subgroup.
 */
const findSubgroup = (
  subgroups: PaletteGroup['subgroups'],
  token: TransformedTokenPalette,
) => {
  const type = getSubgroupName(token);

  return subgroups.find(({ name }) => name === type);
};

const getSubgroupName = (token: TransformedTokenPalette): PaletteCategory => {
  let name;

  switch (token.attributes.category) {
    case 'light mode neutral':
    case 'dark mode neutral':
      const { a } = hexToRGBAValues(token.value);
      name = !a || a === 1 ? 'solid' : 'alpha';
      break;
    default:
      name = token.attributes.category;
      break;
  }

  return name;
};

const getGroupName = (token: TransformedTokenPalette): PaletteGroup['name'] => {
  let name;
  switch (token.attributes.category) {
    case 'light mode neutral':
    case 'dark mode neutral':
      name = `${token.attributes.category}s`;
      break;
    default:
      name = 'colors';
      break;
  }

  return name!;
};

/**
 * Add token to a group. Create new group if it does not exist.
 */
const addToGroup = ({
  groups,
  token,
}: {
  groups: PaletteGroup[];
  token: TransformedTokenPalette;
}) => {
  let matchingType = findGroup(groups, token);

  // No matching type, create new one
  if (!matchingType) {
    groups.push({
      name: getGroupName(token),
      subgroups: [],
    });
    matchingType = findGroup(groups, token);
  }

  if (matchingType) {
    let matchingSubgroup = findSubgroup(matchingType.subgroups, token);

    // No matching group, create new one and add token
    if (!matchingSubgroup) {
      matchingType?.subgroups.push({
        name: getSubgroupName(token),
        tokens: [],
      });

      matchingSubgroup = findSubgroup(matchingType.subgroups, token);
    }

    matchingSubgroup?.tokens.push(token);
  }
};

/**
 * Sorts tokens in groups of
 * 1. Color
 * 2. Light mode neutrals
 * 3. Dark mode neutrals
 */
const sortByType = (a: TransformedTokenPalette, b: TransformedTokenPalette) => {
  if (
    !a.attributes.category.includes('neutral') &&
    b.attributes.category.includes('neutral')
  ) {
    return -1;
  }
  if (
    !a.attributes.category.includes('neutral') &&
    b.attributes.category.includes('neutral')
  ) {
    return 1;
  }

  if (
    a.attributes.category === 'light mode neutral' &&
    b.attributes.category === 'dark mode neutral'
  ) {
    return -1;
  }
  if (
    a.attributes.category === 'dark mode neutral' &&
    b.attributes.category === 'light mode neutral'
  ) {
    return 1;
  }

  return 0;
};

const findTokenName = (token: TransformedTokenPalette) =>
  token.path[token.path.length - 1];

const findTokenValue = (token: TransformedTokenPalette) => {
  const number = findTokenName(token).match(/\d+/g)?.[0];

  return number ? parseInt(number) : undefined;
};

/**
 * Sorts token shades from lightest to darkest
 *
 * @example
 * 1. B100
 * 2. B200
 * 3. B300
 */
const sortByShade = (
  a: TransformedTokenPalette,
  b: TransformedTokenPalette,
) => {
  const [aValue, bValue] = [findTokenValue(a)!, findTokenValue(b)!];

  if (aValue < bValue) {
    return -1;
  }
  if (aValue > bValue) {
    return 1;
  }
  return 0;
};

const groupTokens = (tokens: TransformedTokenPalette[]) => {
  const sortedTokens = tokens.sort(sortByShade).sort(sortByType);

  // Group tokens
  let groupedTokens: PaletteGroup[] = [];

  sortedTokens.forEach((token) =>
    addToGroup({
      groups: groupedTokens,
      token,
    }),
  );

  return groupedTokens;
};

const groupedTokens = groupTokens(
  (palettesRaw as unknown) as TransformedTokenPalette[],
);

export default groupedTokens;
